<template>
  <div>
    <ProductProvider v-slot="{ items: productItems }">
      <el-table
        :data="items"
        :show-summary="showSummary"
        :summary-method="getSummaries"
      >
        <el-table-column min-width="45" :class-name="$style.tableColumn">
          <template v-slot="{ $index }">
            <div :style="{ display: 'flex', alignItems: 'center' }">
              <el-button
                v-if="!readonly"
                icon="el-icon-close"
                type="danger"
                size="mini"
                circle
                :style="{ flexShrink: '0', marginRight: '0.5rem' }"
                @click="removeItem($index)"
              ></el-button>
              <div :style="{ fontSize: '1rem', fontWeight: 'bold' }">
                {{ $index + 1 }}
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          label="ชนิดทอง"
          prop="type"
          header-align="center"
          min-width="110"
          :label-class-name="$style.tableLabel"
          :class-name="$style.tableColumn"
        >
          <template v-slot="{ row }">
            <el-select
              v-if="!readonly"
              v-model="row.product"
              size="small"
              filterable
              value-key="id"
              @touchend.native="
                e => {
                  e.target.readOnly = false
                }
              "
            >
              <el-option
                v-for="(item, index) in productItems"
                :key="index"
                :label="item.name"
                :value="item"
              />
            </el-select>
            <template v-else>{{ row.product && row.product.name }}</template>
          </template>
        </el-table-column>
        <el-table-column
          prop="goldPattern"
          header-align="center"
          align="left"
          min-width="240"
          :label-class-name="$style.tableLabel"
          :class-name="$style.tableColumn"
        >
          <template slot="header">ลายทอง</template>
          <template v-slot="{ row }">
            <el-input
              v-if="!readonly"
              v-model="row.goldPattern"
              size="small"
              :class="$style.numberInput"
            ></el-input>
            <template v-else>
              {{ row.goldPattern }}
            </template>
          </template>
        </el-table-column>
        <el-table-column
          label="% ทอง"
          prop="percent"
          header-align="right"
          align="right"
          min-width="75"
          :label-class-name="$style.tableLabel"
          :class-name="$style.tableColumn"
        >
          <template v-slot="{ row }">
            <span :class="utilStyles.textSecondary">
              {{ row.product && row.product.percent | $_number_percent }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          label="น้ำหนักทอง (บาท)"
          prop="weight"
          header-align="right"
          align="right"
          min-width="125"
          :label-class-name="$style.tableLabel"
          :class-name="$style.tableColumn"
        >
          <template v-slot="{ row }">
            <NumberInput
              v-if="!readonly"
              v-model="row.weight"
              size="small"
              :min="0"
              :class="elementUiStyles.numberInput"
            ></NumberInput>
            <template v-else>
              {{ row.weight | $_number_formattedNumber }}
            </template>
          </template>
        </el-table-column>
        <el-table-column
          label="น้ำหนักจริง (กรัม)"
          prop="realWeight"
          header-align="right"
          align="right"
          min-width="125"
          :label-class-name="$style.tableLabel"
          :class-name="$style.tableColumn"
        >
          <template v-slot="{ row }">
            <NumberInput
              v-if="!readonly"
              v-model="row.realWeight"
              size="small"
              :min="0"
              :class="elementUiStyles.numberInput"
            ></NumberInput>
            <template v-else>
              {{ row.realWeight | $_number_formattedNumber }}
            </template>
          </template>
        </el-table-column>
        <el-table-column
          label="ราคาซื้อ (บาท)"
          prop="price"
          header-align="right"
          align="right"
          min-width="150"
          :label-class-name="$style.tableLabel"
          :class-name="$style.tableColumn"
        >
          <template v-slot="{ row }">
            <NumberInput
              v-if="!readonly"
              v-model="row.price"
              size="small"
              :min="0"
              :class="[
                elementUiStyles.numberInput,
                row.price !== null &&
                  row.price > row.priceCeiling &&
                  $style.dangerInput
              ]"
            ></NumberInput>
            <template v-else>
              {{ row.price | $_number_formattedNumber }}
            </template>
          </template>
        </el-table-column>
        <el-table-column
          label="เพดานราคา"
          prop="priceCeiling"
          header-align="right"
          align="right"
          min-width="125"
          :label-class-name="$style.tableLabel"
          :class-name="$style.tableColumn"
        >
          <template v-slot="{ row }">
            {{ row.priceCeiling | $_number_formattedNumber }}
          </template>
        </el-table-column>
      </el-table>
    </ProductProvider>

    <template v-if="showSummary">
      <el-row
        v-if="items.length"
        type="flex"
        :gutter="16"
        :style="{ flexWrap: 'wrap', margin: '1.5rem 0' }"
      >
        <el-col :md="{ span: 12, offset: 12 }">
          <el-row
            type="flex"
            :gutter="16"
            :style="{
              fontSize: '1.25rem',
              fontWeight: 'bold',
              flexWrap: 'wrap'
            }"
          >
            <el-col
              :sm="12"
              :style="{ marginBottom: $mq === 'sm' ? '0.5rem' : null }"
            >
              ยอดเงินที่ต้องจ่ายให้ลูกค้า
            </el-col>
            <el-col
              :sm="12"
              :style="{ textAlign: 'right' }"
              :class="
                getTotalPrice() > getTotalPriceCeiling() &&
                  utilStyles.colorDanger
              "
              >{{ getTotalPrice() | $_number_formattedNumber }}</el-col
            >
          </el-row>
        </el-col>
      </el-row>

      <div
        v-if="isNoteRequired"
        :style="{ marginTop: '1rem' }"
        :class="utilStyles.colorDanger"
      >
        **ราคาซื้อสูงกว่าราคามาตรฐาน โปรดตรวจสอบ และ ระบุหมายเหตุ
      </div>
    </template>
  </div>
</template>

<script>
import numberMixin from '@/mixins/number'
import { NumberInput } from '@/components/form/number-input'
import ProductProvider from './ProductProvider'

export default {
  components: { NumberInput, ProductProvider },

  mixins: [numberMixin],

  props: {
    items: {
      type: Array,
      default: () => []
    },

    readonly: {
      type: Boolean,
      default: false
    },

    showSummary: {
      type: Boolean,
      default: true
    }
  },

  computed: {
    isNoteRequired() {
      return this.items.some(item => item.price > item.priceCeiling)
    }
  },

  methods: {
    getTotalPrice() {
      return this.items.reduce((prev, curr) => prev + curr.price, 0)
    },

    getTotalPriceCeiling() {
      return this.items.reduce((prev, curr) => prev + curr.priceCeiling, 0)
    },

    removeItem(index) {
      this.$emit('remove', index)
    },

    getSummaries({ columns, data }) {
      const sums = []

      columns.forEach((column, index) => {
        if (index === 2) {
          sums[index] = (
            <div
              style={{
                fontSize: '1.125rem',
                fontWeight: 'bold',
                textAlign: 'right'
              }}
            >
              รวม
            </div>
          )
          return
        }

        if (index === 2) return

        const values = data.map(item => {
          return Number(item[column.property])
        })
        if (!values.every(value => isNaN(value))) {
          const sum = values.reduce((prev, curr) => {
            const value = Number(curr)
            if (!isNaN(value)) {
              return prev + value
            } else {
              return prev
            }
          }, 0)

          sums[index] = this.$_number_toFormattedNumber(sum)
        }
      })

      return sums
    }
  }
}
</script>

<style lang="scss" module>
.tableLabel {
  word-break: normal !important;
}

.tableColumn {
  :global(.cell) {
    padding: 0 0.25rem;
  }
}

.dangerInput {
  :global(.el-input__inner) {
    color: $--color-danger;
  }
}
</style>

<style
  src="@/assets/scss/modules/element-ui.module.scss"
  lang="scss"
  module="elementUiStyles"
></style>

<style
  src="@/assets/scss/modules/utilities.module.scss"
  lang="scss"
  module="utilStyles"
></style>
